/**
 * Response shapes from biller-api, ported verbatim from the app this replaces
 * (`web/src/api/types.ts`). Deliberately not "improved": the API is the
 * authority, and a nicer client-side shape would only add a mapping layer that
 * drifts out of sync the first time the API changes.
 *
 * snake_case is kept for the same reason — it is what comes over the wire, and
 * renaming at the boundary hides which fields are ours and which are theirs.
 */

/**
 * A gateway as the portal shows it.
 *
 * `gateway_name` is the agreed display form — `LABEL (ID)` when an internal
 * user has named it, the bare id when nobody has. It is composed server-side
 * on purpose: two screens formatting it differently would leave a reader
 * unable to tell whether they are looking at the same gateway.
 */
export interface Gateway {
  readonly gateway_id: string
  readonly gateway_label?: string
  readonly gateway_name: string
}

/** What an organisation IS, fixed at creation (FR-40): a provider — a service
 *  provider, what the portal calls a biller — holds gateways; a beneficiary
 *  holds slices of a gateway. */
export type OrganisationKind = 'provider' | 'beneficiary'
export const ORGANISATION_KINDS: readonly OrganisationKind[] = ['provider', 'beneficiary']

export interface Biller {
  readonly biller_id: string
  readonly name: string
  readonly kind: OrganisationKind
  /** Settlement cut-off "HH:MM" (Africa/Douala), or absent — powers the
   *  "Settlement day" checkbox only (FR-41). */
  readonly settlement_day_start?: string
  /** Live slices a beneficiary organisation holds; 0 for a biller. */
  readonly slice_count: number
  readonly gateways: readonly Gateway[]
  /** Deactivated billers are listed — the admin screen is where they get
   *  reactivated — so the flag, not the absence of a row, is the signal. */
  readonly is_active: boolean
  readonly created_at: string
  /** Counted in Keycloak, which is the source of truth for users. */
  readonly user_count: number
}

/** One row of the gateway mapping screen. */
export interface GatewayEntry {
  readonly gateway_id: string
  /** Core's own connector name — internal responses only, labelling context. */
  readonly core_name?: string
  readonly gateway_label: string
  readonly gateway_name: string
  readonly biller_id: string
  readonly biller_name: string
  /** Neither a label nor an owner: nothing has been done with it yet. */
  readonly unmapped: boolean
  /** V1.1 (internal catalogue): beneficiary identities observed on the
   *  gateway, and how many of them no organisation holds yet. */
  readonly beneficiary_count?: number
  readonly unassigned_beneficiary_count?: number
}

/** One beneficiary identity observed on a gateway (internal only, FR-42). */
export interface BeneficiaryLine {
  readonly beneficiary_code: string
  readonly beneficiary_name: string
  readonly count: number
  /** Holding organisation code, '' when unassigned. */
  readonly biller_id: string
  readonly biller_name: string
}

/** One slice an organisation holds: its identity on one gateway (V1.1). */
export interface BillerSlice {
  readonly gateway_id: string
  readonly beneficiary_code: string
}

export interface BillerSlices {
  readonly biller_id: string
  readonly kind: OrganisationKind
  readonly slices: readonly BillerSlice[]
}

export interface GatewayBeneficiaries {
  readonly gateway_id: string
  readonly beneficiaries: readonly BeneficiaryLine[]
  /** The gateway's transactions that carry no identity at all. */
  readonly without_identity: number
}

export interface Profile {
  readonly user_id: string
  readonly email: string
  readonly full_name: string
  readonly roles: readonly string[]
  readonly kind: 'internal' | 'biller'
  readonly can_manage_users: boolean
  readonly can_switch_biller: boolean
  readonly biller_id?: string
  readonly organisation?: string
  readonly assigned_gateways: readonly Gateway[]
  /** V1.1: the organisation's kind and settlement cut-off, when one is selected. */
  readonly organisation_kind?: OrganisationKind
  readonly settlement_day_start?: string
}

/**
 * The status vocabulary a biller may FILTER by.
 *
 * PENDING is never imported into the warehouse, and REVERSED is being removed
 * by a forthcoming Core update. A filter value that can never match is worse
 * than no filter value: the user selects it, sees an empty table, and
 * concludes their data is missing.
 *
 * `statusTone` still maps all four, so a historical REVERSED row returned by
 * the API renders correctly rather than falling through to a default.
 */
export const TX_STATUSES = ['SUCCESS', 'FAILED'] as const
export type TxStatus = (typeof TX_STATUSES)[number]

export interface Transaction {
  readonly ptn: string
  readonly created_at: string
  readonly gateway_id: string
  /** FR-39: the biller-facing name Ops set on the service in Core, resolved
   *  by the API at read time. Always present; empty while none is set, which
   *  the screen renders as "n/a". */
  readonly service_label: string
  /** FR-38: grantable since 2026-08-25 — present only when the page's policy
   *  exposes the field (`scope.exposed_fields`), absent otherwise. */
  readonly gateway_name?: string
  /** V1.1. Internal users only (FR-42) — never present for an organisation's user. */
  readonly beneficiary_code?: string
  readonly beneficiary_name?: string
  /** V1.1. The receipt fields, grantable under the policy (FR-43). */
  readonly bill_number?: string
  /** V1.1 (FR-41): the settlement day the payment belongs to, YYYY-MM-DD in
   *  business time — derived by the API for a beneficiary organisation with
   *  a cut-off, absent otherwise. */
  readonly settlement_day?: string
  readonly svc_nmb?: string
  readonly cus_nmb?: string
  readonly service_name?: string
  readonly service_type?: string
  readonly amount: number
  readonly currency: string
  readonly transaction_status: TxStatus
  readonly payer_reference: string
  /** MPAY-33568: the service provider's own transaction id. Default tier
   *  (AB-39): always present, empty until the provider has called back. */
  readonly provider_txid: string
  readonly payee_note?: string
  /** The configurable tier (TRD 8002). Absent — not empty — when the
   *  requester's field exposure policy withholds the field. */
  readonly fee?: number
  readonly collected_total?: number
  readonly paid_at?: string
  readonly status_updated_at?: string
  readonly source_status?: string
  /** MPAY-33569: the collecting partner's BID, grantable and sensitive. */
  readonly collector_company_bid?: string
  /** MPAY-33757: the customer data (CDATA), grantable and sensitive; absent
   *  on a payment that carries none. Detail panel and export only (AB-41). */
  readonly cdata?: string

}

export interface Summary {
  readonly total_count: number
  readonly total_amount_xaf: number
  readonly filtered_count: number
  readonly filtered_amount_xaf: number
  readonly success_count: number
  readonly failed_count: number
  readonly pending_count: number
  readonly reversed_count: number
}

export interface Pagination {
  readonly page: number
  readonly page_size: number
  readonly total_pages: number
  readonly total_records: number
}

export interface TransactionsResponse {
  readonly transactions: readonly Transaction[]
  readonly summary: Summary
  readonly pagination: Pagination
  readonly scope: {
    readonly biller_id: string
    readonly organisation: string
    readonly gateways: readonly Gateway[]
    /** True when an internal user has selected no biller and is looking at
     *  every gateway on the platform. */
    readonly unrestricted: boolean
    /** FR-38: the grantable fields this requester is entitled to, in catalogue
     *  order. The screen renders columns and detail rows from this list,
     *  never from which keys the rows happen to carry; grantable filters
     *  follow filterable_fields. */
    readonly exposed_fields: readonly string[]
    /** MPAY-33568: the grantable fields this requester may FILTER on, those
     *  exposed on every row. A field granted for some service types only is
     *  in exposed_fields and not here. */
    readonly filterable_fields?: readonly string[]
  }
  /** MPAY-33571: what the service filters can offer, within the scope and the
   *  dates whatever is filtered. No service types where the field is withheld. */
  readonly filter_options?: {
    readonly service_labels: readonly string[]
    readonly service_types?: readonly string[]
  }
}

export interface PortalUser {
  readonly id: string
  readonly email: string
  readonly first_name: string
  readonly last_name: string
  readonly full_name: string
  readonly enabled: boolean
  readonly biller_id: string
  readonly mfa_enrolled: boolean
  readonly pending_actions: readonly string[]
  readonly created_at?: string
}

export interface UsersResponse {
  readonly users: readonly PortalUser[]
  readonly biller_id: string
}

export interface AuditEvent {
  readonly id: number
  readonly at: string
  readonly user_id: string
  readonly email: string
  readonly biller_code: string
  readonly event_type: string
  readonly ip_address: string
  readonly metadata?: Readonly<Record<string, unknown>>
}

/** Every biller-api response is wrapped; `bffRequest` returns the whole
 *  envelope, and `unwrap` in client.ts takes the `data`. */
export interface Envelope<T> {
  readonly data: T
  readonly meta: { readonly request_id: string; readonly timestamp: string }
  readonly error: { readonly code: string; readonly message: string } | null
}

/**
 * One field an internal user may grant to a biller (TRD 8002, field exposure).
 *
 * The catalogue comes from the API rather than being listed here on purpose.
 * A field the service does not consider configurable must not be offerable by
 * the screen, and the only way to guarantee that is to never invent the list
 * on this side.
 */
export interface ConfigurableField {
  readonly name: string
  readonly label: string
  readonly help: string
  /** Discloses something beyond the biller's own business — third-party
   *  personal data, or Smobilpay's commercial margin. Rendered with a warning. */
  readonly sensitive: boolean
}

/** One grant. An absent service_type means "any". */
export interface FieldExposureRule {
  readonly id: string
  readonly field: string
  readonly biller_code?: string
  readonly service_type?: string
  readonly note: string
  readonly created_by: string
  readonly created_at: string
}

export interface FieldExposureResponse {
  readonly fields: readonly ConfigurableField[]
  readonly rules: readonly FieldExposureRule[]
  /** Every service type the warehouse has carried, in Core's public
   *  vocabulary. The grant form offers exactly this list — a rule is matched
   *  by string equality per row, so free text that drifted from the data
   *  would grant nothing, silently. */
  readonly service_types: readonly string[]
}

/** The closed topic list POST /v1/support accepts. The API owns the list;
 *  this type mirrors it so a drift is a compile error next to the form. */
export const SUPPORT_TOPICS = ['ACCESS', 'TRANSACTIONS', 'EXPORTS', 'USERS', 'OTHER'] as const
export type SupportTopic = (typeof SUPPORT_TOPICS)[number]

export interface SupportRequestInput {
  readonly topic: SupportTopic
  readonly message: string
  readonly attachments: readonly File[]
}

/** The gateway dimension is deliberately absent: the API still accepts it,
 *  but the portal no longer writes gateway-restricted rules — nobody has
 *  needed one, and a second free-text dimension was accident surface on the
 *  most consequential form in the product. */
export interface GrantFieldExposureInput {
  readonly field: string
  readonly biller_code?: string
  readonly service_type?: string
  readonly note: string
}
